import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  ConfigProvider,
  DatePicker,
  Divider,
  Input,
  Modal,
  notification,
  Popconfirm,
  Space,
  Table,
  Tabs,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import { BarChartOutlined, DeleteOutlined, EditOutlined, EyeOutlined, FileTextOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import viVN from 'antd/locale/vi_VN';
import { InvoiceNotification } from '../types/invoiceNotification';
import { InvoiceNotificationService } from '../services/invoiceNotificationService';
import { parseExcelToInvoiceNotifications } from '../utils/invoiceExcelUtils';
import { parseJsonToInvoiceNotifications } from '../utils/invoiceJsonUtils';
import { isCukcukInvoiceFile } from '../utils/cukcukInvoiceImportUtils';
import { storeService } from '../services/storeService';
import dayjs, { Dayjs } from 'dayjs';
import InvoiceStatistics from '../components/InvoiceStatistics';

const { Search } = Input;
const { Title } = Typography;
const { RangePicker } = DatePicker;

const invoiceNotificationService = new InvoiceNotificationService();

const SEARCH_DEBOUNCE_MS = 300;
let cachedStoreNameMap: Record<string, string> | null = null;

interface InvoiceSummaryRow {
  key: string;
  index: number;
  invoiceCode: string;
  billNumber: string;
  shiftCode: string;
  guestCount: number;
  employeeName: string;
  tableName: string;
  paymentMethodName: string;
  vatInfo: string;
  eInvoiceNumber: string;
  totalAmount: number;
  checkInDate: string;
  checkInTime: string;
  checkOutDate: string;
  checkOutTime: string;
  store: string;
  orderCode: string;
  currency: string;
  rows: InvoiceNotification[];
}

const InvoiceManagement: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<InvoiceNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [clearingAll, setClearingAll] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRows, setDetailRows] = useState<InvoiceNotification[]>([]);
  const [detailHeader, setDetailHeader] = useState<InvoiceNotification | null>(null);
  const [storeNameMap, setStoreNameMap] = useState<Record<string, string>>({});
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(() => [
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const isMountedRef = useRef(true);
  const hasInitializedRef = useRef(false);
  const fetchPromiseRef = useRef<Promise<void> | null>(null);
  const fetchRequestIdRef = useRef(0);

  const formatIposMoney = useCallback((value?: number) => {
    if (value === undefined || value === null) {
      return '0 ₫';
    }

    return `${value.toLocaleString('vi-VN')} ₫`;
  }, []);

  const fetchInvoices = useCallback(async () => {
    if (fetchPromiseRef.current) {
      return fetchPromiseRef.current;
    }

    const requestId = ++fetchRequestIdRef.current;

    const fetchTask = (async () => {
      if (isMountedRef.current) {
        setLoading(true);
      }

      try {
        const data = await invoiceNotificationService.getAllNotifications();

        if (!isMountedRef.current || requestId !== fetchRequestIdRef.current) {
          return;
        }

        setInvoices(data);
      } catch (error) {
        if (!isMountedRef.current || requestId !== fetchRequestIdRef.current) {
          return;
        }

        notification.error({
          message: 'Lỗi',
          description: 'Không thể tải danh sách hóa đơn.',
        });
      } finally {
        if (requestId === fetchRequestIdRef.current && isMountedRef.current) {
          setLoading(false);
        }
        fetchPromiseRef.current = null;
      }
    })();

    fetchPromiseRef.current = fetchTask;
    return fetchTask;
  }, []);

  const loadStoreNames = useCallback(async () => {
    if (cachedStoreNameMap) {
      setStoreNameMap(cachedStoreNameMap);
      return;
    }

    try {
      const stores = await storeService.getStores();
      const mapped = stores.reduce((acc, store) => {
        if (store.id && store.name) {
          acc[store.id] = store.name;
        }
        return acc;
      }, {} as Record<string, string>);

      cachedStoreNameMap = mapped;
      if (isMountedRef.current) {
        setStoreNameMap(mapped);
      }
    } catch (error) {
      console.error('Cannot load store mapping:', error);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;
    void fetchInvoices();
    void loadStoreNames();
  }, [fetchInvoices, loadStoreNames]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchInput.trim().toLowerCase());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput]);

  const resolveStoreName = useCallback((value?: string) => {
    if (!value) {
      return '-';
    }

    return storeNameMap[value] || value;
  }, [storeNameMap]);

  const resolveShiftCode = useCallback((value?: string) => {
    if (!value) {
      return '-';
    }

    const trimmed = value.trim();

    if (trimmed.length <= 8) {
      return trimmed;
    }

    return `#${trimmed.slice(-5)}`;
  }, []);

  const resolveInvoiceCode = useCallback((row: InvoiceSummaryRow) => {
    const code = String(row.invoiceCode || '').trim();
    if (code.startsWith('#')) {
      return code;
    }

    const orderCode = String(row.orderCode || '').trim();
    if (orderCode.length >= 5) {
      return `#${orderCode.slice(-5)}`;
    }

    if (code) {
      return `#${code}`;
    }

    return '-';
  }, []);

  const resolveInvoiceCodeFromDetail = useCallback((record?: InvoiceNotification | null) => {
    if (!record) {
      return '-';
    }

    const code = String(record.invoiceCode || '').trim();
    if (code.startsWith('#')) {
      return code;
    }

    const orderCode = String(record.orderCode || '').trim();
    if (orderCode.length >= 5) {
      return `#${orderCode.slice(-5)}`;
    }

    if (code) {
      return `#${code}`;
    }

    return '-';
  }, []);

  const parseVnDate = useCallback((dateText?: string) => {
    if (!dateText || dateText === '-') {
      return null;
    }

    const parsed = dayjs(dateText, 'DD/MM/YYYY', true);
    return parsed.isValid() ? parsed : null;
  }, []);

  const handleViewDetail = useCallback((record: InvoiceNotification) => {
    const invoiceKey = record.orderCode || record.invoiceCode;
    const relatedRows = invoices
      .filter(item => (item.orderCode || item.invoiceCode) === invoiceKey)
      .sort((a, b) => (Number(a.stt || 0) || 0) - (Number(b.stt || 0) || 0));

    setDetailHeader(relatedRows[0] || record);
    setDetailRows(relatedRows);
    setDetailVisible(true);
  }, [invoices]);

  const handleDeleteInvoiceGroup = useCallback(async (rows: InvoiceNotification[]) => {
    try {
      const ids = rows.map(row => row.id).filter(Boolean) as string[];
      if (!ids.length) {
        return;
      }

      setDeletingId(ids[0]);
      for (const id of ids) {
        await invoiceNotificationService.deleteNotification(id);
      }

      const deletedIdSet = new Set(ids);
      setInvoices((prev) => prev.filter((item) => !item.id || !deletedIdSet.has(item.id)));

      setDetailRows((prev) => prev.filter((item) => !item.id || !deletedIdSet.has(item.id)));
      setDetailHeader((prev) => {
        if (!prev?.id) {
          return prev;
        }
        return deletedIdSet.has(prev.id) ? null : prev;
      });

      notification.success({
        message: 'Thành công',
        description: `Đã xóa ${ids.length} dòng của hóa đơn.`,
      });
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: 'Không thể xóa hóa đơn.',
      });
    } finally {
      setDeletingId(null);
    }
  }, []);

  const handleDeleteAll = useCallback(async () => {
    try {
      setClearingAll(true);
      const deletedCount = await invoiceNotificationService.deleteAllNotifications();
      notification.success({
        message: 'Thành công',
        description: `Đã xóa ${deletedCount} dòng hóa đơn.`,
      });
      setInvoices([]);
      setDetailRows([]);
      setDetailHeader(null);
      setDetailVisible(false);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: 'Không thể xóa toàn bộ dữ liệu hóa đơn.',
      });
    } finally {
      setClearingAll(false);
    }
  }, []);

  const handleInvoiceFileUpload = useCallback((file: File) => {
    setLoading(true);

    const isJsonFile = file.name.toLowerCase().endsWith('.json') || file.type === 'application/json';

    const processUploadedFile = async () => {
      if (isJsonFile) {
        const parsedInvoices = await parseJsonToInvoiceNotifications(file);
        if (!parsedInvoices.length) {
          Modal.warning({
            title: 'File chưa có dữ liệu hóa đơn',
            content: (
              <div>
                <p>Không tìm thấy dòng dữ liệu hợp lệ trong file.</p>
                <p>Vui lòng kiểm tra lại cấu trúc file JSON/Excel export.</p>
              </div>
            ),
          });
          return null;
        }

        return invoiceNotificationService.batchUpsertFromExcel(parsedInvoices);
      }

      if (await isCukcukInvoiceFile(file)) {
        Modal.info({
          title: 'File hóa đơn CUKCUK',
          content:
            'Import CUKCUK đã chuyển sang trang riêng để xem trước, kiểm tra mapping option và tránh import trùng.',
          okText: 'Mở Import CUKCUK',
          onOk: () => navigate('/import-cukcuk'),
        });
        return null;
      }

      const parsedInvoices = await parseExcelToInvoiceNotifications(file);
      if (!parsedInvoices.length) {
        Modal.warning({
          title: 'File chưa có dữ liệu hóa đơn',
          content: (
            <div>
              <p>Không tìm thấy dòng dữ liệu hợp lệ trong file.</p>
              <p>Vui lòng kiểm tra lại cấu trúc file JSON/Excel export.</p>
            </div>
          ),
        });
        return null;
      }

      return invoiceNotificationService.batchUpsertFromExcel(parsedInvoices);
    };

    processUploadedFile()
      .then((result) => {
        if (!result) {
          return;
        }

        notification.success({
          message: 'Upload thành công',
          description: `Đã thêm ${result.added}, cập nhật ${result.updated} hóa đơn.${
            result.skipped > 0 ? ` Bỏ qua ${result.skipped} dòng đã tồn tại.` : ''
          }${result.errors.length > 0 ? ` Có ${result.errors.length} lỗi.` : ''}`,
        });

        if (result.errors.length > 0) {
          Modal.error({
            title: 'Có lỗi khi xử lý file',
            width: 680,
            content: (
              <div>
                <p>Một số dòng chưa lưu được:</p>
                <ul>
                  {result.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            ),
          });
        }

        if (result.added > 0 || result.updated > 0) {
          void fetchInvoices();
        }
      })
      .catch((error) => {
        notification.error({
          message: 'Lỗi upload',
          description: error?.message || 'Không thể đọc hoặc lưu file hóa đơn.',
        });
      })
      .finally(() => {
        if (isMountedRef.current) {
          setLoading(false);
        }
      });

    return false;
  }, [fetchInvoices]);

  const invoiceSummaries = useMemo<InvoiceSummaryRow[]>(() => {
    const grouped = new Map<string, InvoiceNotification[]>();

    invoices.forEach((item) => {
      const key = item.orderCode || item.invoiceCode;
      if (!key) {
        return;
      }

      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(item);
    });

    const summary = Array.from(grouped.entries()).map(([key, rows]) => {
      const first = rows[0];
      const total = first.totalAmount ?? rows.reduce((sum, row) => sum + (row.lineTotal || 0), 0);

      return {
        key,
        index: 0,
        invoiceCode: first.invoiceCode || '-',
        billNumber: first.billNumber || '-',
        shiftCode: resolveShiftCode(first.shiftName || first.shiftCode),
        guestCount: first.guestCount ?? 0,
        employeeName: first.employeeName || '-',
        tableName: first.tableName || '-',
        paymentMethodName: first.paymentMethodName || '-',
        vatInfo: first.vatInfo || '-',
        eInvoiceNumber: first.eInvoiceNumber || '-',
        totalAmount: total,
        checkInDate: first.checkInDate || '-',
        checkInTime: first.checkInTime || '-',
        checkOutDate: first.checkOutDate || '-',
        checkOutTime: first.checkOutTime || '-',
        store: first.store || '-',
        orderCode: first.orderCode || key,
        currency: first.currency || 'VND',
        rows,
      };
    });

    summary.sort((a, b) => {
      const parseDateTime = (date: string, time: string) => {
        if (!date || date === '-') return null;
        const dateStr = date.trim();
        const timeStr = time && time !== '-' ? time.trim() : '00:00:00';
        return dayjs(`${dateStr} ${timeStr}`, 'DD/MM/YYYY HH:mm:ss', true).isValid()
          ? dayjs(`${dateStr} ${timeStr}`, 'DD/MM/YYYY HH:mm:ss', true)
          : dayjs(`${dateStr} ${timeStr}`, 'DD/MM/YYYY HH:mm', true).isValid()
            ? dayjs(`${dateStr} ${timeStr}`, 'DD/MM/YYYY HH:mm', true)
            : null;
      };

      const aTime = parseDateTime(a.checkInDate, a.checkInTime);
      const bTime = parseDateTime(b.checkInDate, b.checkInTime);

      if (aTime && bTime) return bTime.valueOf() - aTime.valueOf();
      if (aTime) return 1;
      if (bTime) return -1;
      return b.billNumber.localeCompare(a.billNumber);
    });

    return summary.map((row, idx) => ({
      ...row,
      index: idx + 1,
    }));
  }, [invoices, resolveShiftCode]);

  const filteredInvoices = useMemo(() => {
    const [startDate, endDate] = dateRange || [];
    const hasDateFilter = Boolean(startDate || endDate);

    const dateFiltered = hasDateFilter
      ? invoiceSummaries.filter((item) => {
        const rowDate = parseVnDate(item.checkOutDate) || parseVnDate(item.checkInDate);
        if (!rowDate) {
          return false;
        }

        const validStart = startDate ? !rowDate.isBefore(startDate.startOf('day')) : true;
        const validEnd = endDate ? !rowDate.isAfter(endDate.endOf('day')) : true;

        return validStart && validEnd;
      })
      : invoiceSummaries;

    const keyword = debouncedSearchTerm;
    if (!keyword) {
      return dateFiltered;
    }

    return dateFiltered.filter(item => {
      return (
        item.invoiceCode.toLowerCase().includes(keyword) ||
        item.billNumber.toLowerCase().includes(keyword) ||
        item.shiftCode.toLowerCase().includes(keyword) ||
        item.employeeName.toLowerCase().includes(keyword) ||
        item.tableName.toLowerCase().includes(keyword) ||
        item.paymentMethodName.toLowerCase().includes(keyword)
      );
    });
  }, [invoiceSummaries, debouncedSearchTerm, dateRange, parseVnDate]);

  const tableSummary = useMemo(() => {
    const totalAmount = filteredInvoices.reduce((sum, row) => sum + (row.totalAmount || 0), 0);
    const totalGuests = filteredInvoices.reduce((sum, row) => sum + (row.guestCount || 0), 0);

    return {
      totalAmount,
      totalInvoices: filteredInvoices.length,
      totalGuests,
    };
  }, [filteredInvoices]);

  const columns = useMemo(() => [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'stt',
      width: 60,
      fixed: 'left' as const,
      render: (value: number) => value,
    },
    {
      title: 'Mã hóa đơn',
      dataIndex: 'invoiceCode',
      key: 'invoiceCode',
      width: 120,
      render: (_: string, record: InvoiceSummaryRow) => resolveInvoiceCode(record),
    },
    {
      title: 'Số hóa đơn',
      dataIndex: 'billNumber',
      key: 'billNumber',
      width: 150,
      render: (value: string) => value || '-',
    },
    {
      title: 'Mã ca',
      dataIndex: 'shiftCode',
      key: 'shiftCode',
      width: 110,
      render: (value: string) => value || '-',
    },
    {
      title: 'Số hóa đơn',
      dataIndex: 'billNumber',
      key: 'billNumber',
      width: 160,
      render: (value: string) => value || '-',
    },
    {
      title: 'Số khách',
      dataIndex: 'guestCount',
      key: 'guestCount',
      width: 100,
      render: (value: number) => value ?? 0,
    },
    {
      title: 'Nhân viên',
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 140,
      render: (value: string) => value || '-',
    },
    {
      title: 'Bàn',
      dataIndex: 'tableName',
      key: 'tableName',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: 'PTTT',
      dataIndex: 'paymentMethodName',
      key: 'paymentMethodName',
      width: 140,
      render: (value: string) => value || '-',
    },
    {
      title: 'Thông tin VAT',
      dataIndex: 'vatInfo',
      key: 'vatInfo',
      width: 130,
      render: (value: string) => value || '-',
    },
    {
      title: 'Số HĐĐT',
      dataIndex: 'eInvoiceNumber',
      key: 'eInvoiceNumber',
      width: 130,
      render: (value: string) => value || '-',
    },
    {
      title: 'Tổng tiền',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 140,
      render: (value: number, record: InvoiceSummaryRow) =>
        value !== undefined ? `${value.toLocaleString('vi-VN')} ${record.currency || 'VND'}` : '-',
    },
    {
      title: 'Thời gian vào',
      key: 'checkInDateTime',
      width: 170,
      render: (_: any, record: InvoiceSummaryRow) => `${record.checkInDate} ${record.checkInTime}`,
    },
    {
      title: 'Thời gian ra',
      key: 'checkOutDateTime',
      width: 170,
      render: (_: any, record: InvoiceSummaryRow) => `${record.checkOutDate} ${record.checkOutTime}`,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 190,
      fixed: 'right' as const,
      render: (_: any, record: InvoiceSummaryRow) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => handleViewDetail(record.rows[0])}>
            Chi tiết
          </Button>
          <Popconfirm
            title="Xác nhận xóa"
            description="Bạn có chắc muốn xóa toàn bộ dòng của hóa đơn này?"
            okText="Xóa"
            cancelText="Hủy"
            onConfirm={() => handleDeleteInvoiceGroup(record.rows)}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={record.rows.some((row) => row.id === deletingId)}
              disabled={record.rows.every((row) => !row.id)}
            >
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ], [deletingId, handleDeleteInvoiceGroup, handleViewDetail, resolveInvoiceCode]);

  const uploadActions = useMemo(() => (
    <div className="admin-page-actions">
      <Tooltip title="Tải file JSON hóa đơn để tự động thêm/cập nhật thông báo vào Firebase">
        <Upload
          accept=".json,.xlsx,.xls"
          showUploadList={false}
          beforeUpload={handleInvoiceFileUpload}
        >
          <Button type="primary" icon={<UploadOutlined />} className="admin-button">
            Upload file JSON hóa đơn
          </Button>
        </Upload>
      </Tooltip>

      <Popconfirm
        title="Xóa toàn bộ dữ liệu"
        description="Bạn có chắc muốn xóa tất cả dữ liệu hóa đơn đã import? Hành động này không thể hoàn tác."
        okText="Xóa hết"
        okButtonProps={{ danger: true, loading: clearingAll }}
        cancelText="Hủy"
        onConfirm={handleDeleteAll}
      >
        <Button danger loading={clearingAll} icon={<DeleteOutlined />} className="admin-button">
          Xóa hết dữ liệu
        </Button>
      </Popconfirm>
    </div>
  ), [clearingAll, handleDeleteAll, handleInvoiceFileUpload]);

  return (
    <ConfigProvider locale={viVN}>
      <AdminLayout>
        <div className="admin-page-header" style={{ marginTop: '24px' }}>
          <Space direction="vertical" size={4}>
            <Title level={2} className="admin-page-title" style={{ marginBottom: '0px', marginTop: '0px' }}>
              <FileTextOutlined /> Quản lý hóa đơn
            </Title>
            <Typography.Text type="secondary">
              Upload file JSON hoặc Excel hóa đơn để lưu Firebase (hỗ trợ export CUKCUK .xls/.xlsx).
            </Typography.Text>
          </Space>
          {uploadActions}
        </div>

        <Tabs
          defaultActiveKey="invoices"
          style={{ marginTop: 8 }}
          items={[
            {
              key: 'invoices',
              label: (
                <Space>
                  <FileTextOutlined />
                  Danh sách hóa đơn
                </Space>
              ),
              children: (
                <>
                  {/* Filter bar for invoice list */}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                    <RangePicker
                      format="DD/MM/YYYY"
                      allowClear
                      placeholder={['Từ ngày', 'Đến ngày']}
                      value={dateRange}
                      disabledDate={(current) => !!current && current.isAfter(dayjs().endOf('day'))}
                      onChange={(dates) => setDateRange((dates as [Dayjs | null, Dayjs | null]) || null)}
                    />
                    <Search
                      placeholder="Nhập mã hoặc số HĐ"
                      allowClear
                      onChange={(e) => setSearchInput(e.target.value)}
                      style={{ width: 280 }}
                      prefix={<SearchOutlined />}
                    />
                  </div>

                  <div className="admin-form-section">
                    <div className="admin-table-container">
                      <Table
                        className="admin-table"
                        rowKey="key"
                        loading={loading}
                        columns={columns}
                        dataSource={filteredInvoices}
                        size="middle"
                        rowClassName={(_, index) => (index % 2 === 0 ? 'invoice-row-even' : 'invoice-row-odd')}
                        scroll={{ x: 2300, y: 'calc(100vh - 320px)' }}
                        pagination={{
                          pageSize: 20,
                          showSizeChanger: true,
                          showTotal: (total) => `Tổng số: ${total} hóa đơn`,
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px', borderTop: '1px solid #f0f0f0', fontWeight: 600 }}>
                      Tổng tiền: {tableSummary.totalAmount.toLocaleString('vi-VN')} đ &nbsp; - &nbsp;
                      Tổng hóa đơn: {tableSummary.totalInvoices} &nbsp; - &nbsp;
                      Tổng số khách: {tableSummary.totalGuests}
                    </div>
                  </div>
                </>
              ),
            },
            {
              key: 'statistics',
              label: (
                <Space>
                  <BarChartOutlined />
                  Thống kê & Phân tích
                </Space>
              ),
              children: <InvoiceStatistics invoices={invoices} />,
            },
          ]}
        />

        <Modal
          title="Chi tiết hóa đơn"
          open={detailVisible}
          onCancel={() => setDetailVisible(false)}
          footer={null}
          width={800}
        >
          {detailHeader && (
            <div style={{ marginBottom: '12px', padding: '12px 14px', background: '#fafafa', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <Typography.Text style={{ fontSize: '16px', fontWeight: 700 }}>
                    {resolveInvoiceCodeFromDetail(detailHeader)} - Số HĐ: {detailHeader.billNumber || '-'}
                  </Typography.Text>
                  <div style={{ fontSize: '14px', marginTop: '4px' }}>
                    {detailHeader.tableName || '-'} - {formatIposMoney(detailHeader.totalAmount)}
                  </div>
                  <div style={{ color: '#7a7a7a', marginTop: '4px', maxWidth: '520px', whiteSpace: 'pre-line', fontSize: '13px' }}>
                    {detailHeader.note || '-'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '16px' }}>{detailHeader.checkOutDate || '-'}</div>
                  <div style={{ fontSize: '16px', lineHeight: 1.1 }}>{detailHeader.checkOutTime || '-'}</div>
                </div>
              </div>
            </div>
          )}

          <div style={{ maxHeight: '360px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '6px 12px' }}>
            {detailRows.map((row, index) => (
              <div key={row.lineKey || `${row.orderCode}-${row.itemId}-${index}`} style={{ borderBottom: '1px solid #f0f0f0', padding: '10px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 500, fontSize: '15px' }}>
                  <span>(x{row.quantity || 0}) {row.itemName || '-'}</span>
                  <span>{formatIposMoney(row.lineTotal)}</span>
                </div>
                {row.note && (
                  <div style={{ marginTop: '2px', paddingLeft: '2px', color: '#666', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <EditOutlined /> {row.note}
                  </div>
                )}
                {row.selectedOptions && row.selectedOptions.length > 0 && (
                  <div style={{ marginTop: '6px', paddingLeft: '18px' }}>
                    {row.selectedOptions.map((option, optionIndex) => (
                      <div key={`${option.optionId || option.optionName}-${optionIndex}`} style={{ display: 'flex', justifyContent: 'space-between', color: '#666', fontSize: '13px' }}>
                        <span>- (x{option.quantity || 0}) {option.optionName}</span>
                        <span>{formatIposMoney(option.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <Divider style={{ margin: '10px 0' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '16px' }}>
            <span>Thành tiền</span>
            <span>{formatIposMoney(detailRows.reduce((sum, row) => sum + (row.lineTotal || 0), 0))}</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '20px', fontWeight: 700 }}>
            <span>Tổng tiền</span>
            <span>{formatIposMoney(detailHeader?.totalAmount)}</span>
          </div>

          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Phương thức thanh toán:</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px' }}>
            <span>- {detailHeader?.paymentMethodName || '-'} ({detailHeader?.orderCode || '-'})</span>
            <span>{formatIposMoney(detailHeader?.totalAmount)}</span>
          </div>

          <div style={{ marginTop: '4px', color: '#8a8a8a', fontSize: '13px' }}>
            {resolveStoreName(detailHeader?.store)}
          </div>
        </Modal>
      </AdminLayout>
    </ConfigProvider>
  );
};

export default InvoiceManagement;
